import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Plus, Trash2, Lock, Save, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const RULE_TYPES = ["compra", "gasto", "reembolso", "anticipo", "vacante", "orden_compra", "gasto_operativo_directo"];
const APPROVER_KINDS = [
  { code: "rol", label: "Rol" },
  { code: "area", label: "Área (dirección con ese departamento)" },
  { code: "jefe_directo", label: "Jefe directo (reports_to)" },
];
const ROLES = ["corporate_admin", "treasury", "branch_manager", "direccion", "hr_admin", "operativo", "mantenimiento"];

function emptySteps() {
  return [{ orden: 1, approver_kind: "rol", value: "corporate_admin", fallback_role: "" }];
}

function StepsEditor({ steps, onChange }) {
  const update = (idx, patch) => {
    const next = steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(next);
  };
  const add = () => onChange([...steps, { orden: steps.length + 1, approver_kind: "rol", value: "corporate_admin", fallback_role: "" }]);
  const remove = (idx) => onChange(steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, orden: i + 1 })));
  return (
    <div className="space-y-2">
      {steps.map((s, idx) => (
        <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
          <span className="text-xs font-bold text-slate-500 w-14">Paso {idx + 1}</span>
          <select value={s.approver_kind} onChange={(e) => update(idx, { approver_kind: e.target.value })}
            className="border border-slate-200 rounded px-2 py-1 text-xs">
            {APPROVER_KINDS.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
          {s.approver_kind === "jefe_directo" ? (
            <select value={s.fallback_role || ""} onChange={(e) => update(idx, { fallback_role: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-xs flex-1">
              <option value="">Respaldo si no hay jefe: corporate_admin (default)</option>
              {ROLES.map((r) => <option key={r} value={r}>Respaldo: {r}</option>)}
            </select>
          ) : (
            <select value={s.value} onChange={(e) => update(idx, { value: e.target.value })}
              className="border border-slate-200 rounded px-2 py-1 text-xs flex-1">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          {steps.length > 1 && (
            <button onClick={() => remove(idx)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
      ))}
      <button onClick={add} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
        <Plus className="w-3 h-3" /> Agregar paso
      </button>
    </div>
  );
}

function RuleCard({ rule, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [area, setArea] = useState(rule.area || "");
  const [branchId, setBranchId] = useState(rule.branch_id || "");
  const [montoMin, setMontoMin] = useState(rule.monto_min ?? 0);
  const [montoMax, setMontoMax] = useState(rule.monto_max ?? "");
  const [steps, setSteps] = useState(rule.steps);
  const [saving, setSaving] = useState(false);
  const readOnly = !rule.editable || rule.reference_only;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        type: rule.type, area: area || null, branch_id: branchId || null,
        monto_min: parseFloat(montoMin) || 0, monto_max: montoMax === "" ? null : parseFloat(montoMax),
        steps: steps.map((s) => ({ ...s, fallback_role: s.fallback_role || null })),
      };
      await api.put(`/ops/admin/approval-rules/${rule.id}`, body);
      toast.success("Regla actualizada");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("¿Borrar esta regla?")) return;
    try {
      await api.delete(`/ops/admin/approval-rules/${rule.id}`);
      toast.success("Regla eliminada");
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  return (
    <div className={`bg-white rounded-xl border p-4 space-y-2 ${readOnly ? "border-amber-300" : "border-slate-200"}`} data-testid={`rule-card-${rule.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-bold text-slate-900 text-sm">{rule.type}</span>
          {readOnly && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="rule-readonly-badge">
              <Lock className="w-3 h-3" /> Solo lectura — referencia únicamente
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <button onClick={() => setEditing(!editing)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800" data-testid={`btn-editar-regla-${rule.id}`}>
              {editing ? "Cancelar" : "Editar"}
            </button>
            <button onClick={del} className="text-red-500 hover:text-red-700" data-testid={`btn-borrar-regla-${rule.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {rule.note && <p className="text-xs text-slate-500">{rule.note}</p>}

      {!editing ? (
        <div className="text-sm text-slate-700 space-y-1">
          <div>Área: {rule.area || "cualquiera"} · Sucursal: {rule.branch_id || "cualquiera"} · Monto: ${rule.monto_min || 0} — {rule.monto_max ?? "sin límite"}</div>
          <div className="flex flex-wrap gap-1.5">
            {rule.steps.map((s, i) => (
              <span key={i} className="bg-slate-100 rounded-full px-2 py-0.5 text-xs">Paso {s.orden}: {s.approver_kind === "rol" ? s.value : s.approver_kind === "area" ? `área ${s.value}` : `jefe directo${s.fallback_role ? ` (resp. ${s.fallback_role})` : ""}`}</span>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Área (opcional)" value={area} onChange={(e) => setArea(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs" />
            <input placeholder="Sucursal ID (opcional)" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs" />
            <div className="flex gap-1">
              <input placeholder="Monto min" type="number" value={montoMin} onChange={(e) => setMontoMin(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs w-1/2" />
              <input placeholder="Monto max" type="number" value={montoMax} onChange={(e) => setMontoMax(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs w-1/2" />
            </div>
          </div>
          <StepsEditor steps={steps} onChange={setSteps} />
          <button onClick={save} disabled={saving} data-testid={`btn-guardar-regla-${rule.id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-xs hover:bg-yellow-400 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> Guardar
          </button>
        </div>
      )}
    </div>
  );
}

export default function OpsAdminApprovalRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newType, setNewType] = useState("compra");
  const [newSteps, setNewSteps] = useState(emptySteps());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ops/admin/approval-rules");
      setRules(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createRule = async () => {
    try {
      await api.post("/ops/admin/approval-rules", {
        type: newType, steps: newSteps.map((s) => ({ ...s, fallback_role: s.fallback_role || null })),
      });
      toast.success("Regla creada");
      setCreating(false);
      setNewSteps(emptySteps());
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la regla"); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="ops-admin-approval-rules-page">
      <button onClick={() => navigate("/ops/solicitudes")} className="text-sm font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Volver al buzón
      </button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-yellow-500" /> Reglas de aprobación</h1>
          <p className="text-slate-500 text-sm mt-0.5">Quién aprueba cada tipo de solicitud, según área/sucursal/monto — sin tocar código.</p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="btn-nueva-regla"
          className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400">
          <Plus className="w-4 h-4" /> Nueva regla
        </button>
      </div>

      {creating && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <select value={newType} onChange={(e) => setNewType(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-sm">
            {RULE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <StepsEditor steps={newSteps} onChange={setNewSteps} />
          <button onClick={createRule} data-testid="btn-guardar-nueva-regla" className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold">Crear regla</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => <RuleCard key={r.id} rule={r} onSaved={fetchData} onDeleted={fetchData} />)}
        </div>
      )}
    </div>
  );
}
